import Button from '@/components/shared/Button/Button';
import DynamicSVGColor from '@/components/shared/DynamicSVGColor/DynamicSVGColor';
import { TextField } from '@/components/shared/TextField/TextField';

import { blockInfo } from '../../../blockInfo';
import { MILE_COLORS } from '../../../const';
import { JourneyDraft, MileDraft, UpdateDraft } from '../../../draft';
import { DeleteTarget, Selection } from '../../../selection';
import Field from '../Form/Field';
import Section from '../Form/Section';
import MoveButtons from '../MoveButtons/MoveButtons';
import PanelHeader from '../PanelHeader/PanelHeader';

interface MilePanelProps {
  draft: JourneyDraft;
  mileIndex: number;
  update: UpdateDraft;
  locked: boolean;
  onSelect: (selection: Selection) => void;
  onDelete: (target: DeleteTarget) => void;
  onAddStep: (mileKey: string) => void;
}

const MilePanel = ({
  draft,
  mileIndex,
  update,
  locked,
  onSelect,
  onDelete,
  onAddStep,
}: MilePanelProps) => {
  const mile = draft.miles[mileIndex];
  const mileNumber = mileIndex + 1;
  const editMile = (recipe: (mile: MileDraft) => void) => update((d) => recipe(d.miles[mileIndex]));

  const moveStep = (from: number, to: number) =>
    editMile((m) => {
      const [step] = m.steps.splice(from, 1);
      m.steps.splice(to, 0, step);
    });

  return (
    <div className="grid gap-6">
      <PanelHeader
        eyebrow={`Mile ${mileNumber}`}
        title={mile.title || 'Untitled mile'}
        deleteLabel="Delete mile"
        onDelete={locked ? undefined : () => onDelete({ kind: 'mile', mileKey: mile.key })}
      />

      <Section title="General Info">
        <Field label="Title">
          <TextField
            className="!w-full"
            inputClassName="!font-normal"
            value={mile.title}
            placeholder="e.g. Noticing Your Patterns"
            onChange={(e) => editMile((m) => void (m.title = e.target.value))}
          />
        </Field>
        <Field label="Description" hint="What this mile does — shown on the mile’s card">
          <TextField
            isTextArea
            rows={3}
            className="!w-full"
            inputClassName="!font-normal"
            value={mile.description}
            placeholder="Type here..."
            onChange={(e) => editMile((m) => void (m.description = e.target.value))}
          />
        </Field>
        <Field label="Map Colour" hint="Colours the mile’s steps and card on the map">
          <div className="flex gap-3">
            {MILE_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                title={color}
                onClick={() => editMile((m) => void (m.color = color))}
                className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold border-2"
                style={{
                  backgroundColor: `${color}26`,
                  color,
                  borderColor: mile.color === color ? color : 'transparent',
                }}
              >
                {mile.color === color && '✓'}
              </button>
            ))}
          </div>
        </Field>
      </Section>

      <div className="grid gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-neutral-90">
            Steps <span className="text-neutral-40">({mile.steps.length})</span>
          </h3>
          {!locked && (
            <Button
              label="Add Step"
              size="s"
              className="!w-fit px-4"
              prefixIcon={
                <DynamicSVGColor icon="/icons/plus.svg" className="w-5 h-5 !bg-white-100" />
              }
              onClick={() => onAddStep(mile.key)}
            />
          )}
        </div>

        {mile.steps.map((step, stepIndex) => {
          const selectStep = () => onSelect({ kind: 'step', mileKey: mile.key, stepKey: step.key });

          return (
            <div
              key={step.key}
              onClick={selectStep}
              className="flex items-center gap-3 rounded-xl bg-neutral-10 px-3 py-2 cursor-pointer"
            >
              <span className="px-2 py-1 rounded-lg bg-primary-10 text-primary-60 text-sm font-semibold shrink-0">
                Step {mileNumber}.{stepIndex + 1}
              </span>
              {!locked && (
                <MoveButtons
                  canMoveUp={stepIndex > 0}
                  canMoveDown={stepIndex < mile.steps.length - 1}
                  onMoveUp={() => moveStep(stepIndex, stepIndex - 1)}
                  onMoveDown={() => moveStep(stepIndex, stepIndex + 1)}
                />
              )}
              <span className="flex-1 font-semibold text-neutral-90 truncate">
                {step.title || 'Untitled step'}
              </span>
              <span className="flex gap-1 shrink-0">
                {step.blocks.map((block) => {
                  const { icon, typeLabel } = blockInfo(block);
                  return (
                    icon && <img key={block.key} src={icon} alt={typeLabel} className="w-5 h-5" />
                  );
                })}
              </span>
              {!locked && (
                <span onClick={(e) => e.stopPropagation()}>
                  <Button
                    isIcon
                    className="border-none shrink-0"
                    variant="transparent"
                    prefixIcon={<img src="/icons/trash.svg" alt="delete step" />}
                    onClick={() => onDelete({ kind: 'step', mileKey: mile.key, stepKey: step.key })}
                  />
                </span>
              )}
            </div>
          );
        })}

        <button
          type="button"
          onClick={() => onSelect({ kind: 'mileComplete', mileKey: mile.key })}
          className="flex items-center gap-3 rounded-xl border border-dashed border-neutral-30 px-4 py-3 text-left hover:bg-neutral-10"
        >
          <span>🏁</span>
          <span className="font-semibold text-neutral-90">Mile complete</span>
          <span className="text-sm text-neutral-50">Closes the mile, after its last step</span>
        </button>
      </div>
    </div>
  );
};

export default MilePanel;
